import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { ChartModule } from "primeng/chart";
import { MaterialModule } from "../../../core/shared/material.module";
import { PrimeNgModule } from "../../../core/shared/primeng.module";
import { SharedComponents } from "../../shared.module";

/**
 * BarLineChartComponent renders a bar and line chart combination using Chart.js,
 * allowing users to visualize coverage history data with customization options for
 * labels and chart configurations. The component handles dynamic color assignments
 * and responsive chart configurations.
 */
@Component({
  selector: "app-bar-line-chart",
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    ...SharedComponents,
    PrimeNgModule,
    ChartModule,
  ],
  templateUrl: "./bar-line-chart.component.html",
  styleUrls: ["./bar-line-chart.component.scss"],
})
export class BarLineChartComponent implements OnInit {
  @Input() chartData: any;
  @Input() chartMeta: any;

  basicData: any;
  basicOptions: any;
  labels: any = [];
  barChartDataList: any = [];
  rootStyles = getComputedStyle(document.documentElement);

  /**
   * Lifecycle hook that is called when input properties change.
   * If chartData changes, it triggers the chart rebuilding process.
   * @param changes The changes in the input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["chartData"]) {
      this.chartData = changes["chartData"].currentValue;
      if (this.chartData) {
        this.buildChart();
      }
    }
  }

  /**
   * Lifecycle hook called on component initialization. It triggers the chart building process.
   */
  ngOnInit() {
    this.buildChart();
  }

  /**
   * Builds the chart by configuring options, labels, and datasets for rendering.
   * It combines bar and line chart data into a single chart.
   */
  buildChart() {
    const textColor = this.rootStyles.getPropertyValue("--heading-1").trim();
    const textColorSecondary = this.rootStyles
      .getPropertyValue("--black6")
      .trim();

    // Chart Options
    this.basicOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: textColor,
            font: {
              size: 13,
            },
          },
        },
        datalabels: {
          display: false, // Hides the data labels inside the bars
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: textColorSecondary,
            stepSize: this.chartMeta.step_size ? this.chartMeta.step_size : 2,
            display: true,
          },
          grid: {
            drawBorder: false,
          },
          axis: {
            lineColor: this.rootStyles.getPropertyValue("--black").trim(), // Change only the Y-axis line color to black
          },
          title: {
            display: true,
            text: this.chartMeta.y_axis
              ? this.chartMeta.y_axis
              : "Coverage (%)",
            color: textColor,
            font: {
              size: 14,
              weight: "bold",
            },
          },
          borderColor: this.rootStyles.getPropertyValue("--black").trim(),
          borderWidth: 1,
        },
        x: {
          ticks: {
            color: textColorSecondary,
            display: true,
          },
          grid: {
            drawBorder: false,
          },
          title: {
            display: true,
            text: this.chartMeta.x_axis ? this.chartMeta.x_axis : "Versions",
            color: textColor,
            font: {
              size: 14,
              weight: "bold",
            },
          },
          borderColor: this.rootStyles.getPropertyValue("--black").trim(),
          borderWidth: 1,
        },
      },
    };

    this.getBasicData();
  }

  /**
   * Extracts and processes the data for the chart, including labels and data points for both bar and line charts.
   */
  getBasicData() {
    this.labels = this.chartData?.data?.coverage_history?.map(
      (item: any) => `version ${item.version}`
    );
    this.barChartDataList = this.chartData?.data?.coverage_history?.map(
      (item: any) => item.coverage
    );

    // Combined Bar and Line Chart Data
    this.basicData = {
      labels: this.labels,
      datasets: [
        {
          type: "bar",
          label: this.chartMeta.bar_label
            ? this.chartMeta.bar_label
            : "Code Coverage (%)",
          data: [],
          backgroundColor: this.getDynamicColors(),
          borderColor: this.rootStyles.getPropertyValue("--black").trim(),
          borderWidth: 0,
          barThickness: 40,
        },
        {
          type: "line",
          label: this.chartMeta.line_label
            ? this.chartMeta.line_label
            : "Coverage Over Time",
          data: this.barChartDataList,
          borderColor: this.rootStyles.getPropertyValue("--light-gray").trim(),
          tension: 0.4,
          fill: false,
          borderWidth: 2,
          pointBackgroundColor: this.rootStyles
            .getPropertyValue("--blue")
            .trim(), // Blue color for the dots
          pointBorderColor: this.rootStyles.getPropertyValue("--blue").trim(), // Blue border for the dots
          pointBorderWidth: 1, // Optional: Adjusts the border width for the points
          pointRadius: 5, // Optional: Adjusts the size of the points
        },
      ],
    };
  }

  /**
   * Returns an array of dynamic colors used for the bar chart.
   * @returns {string[]} Array of color values.
   */
  getDynamicColors() {
    return [
      this.rootStyles.getPropertyValue("--bar-line-char-color1").trim(),
      this.rootStyles.getPropertyValue("--bar-line-char-color2").trim(),
      this.rootStyles.getPropertyValue("--bar-line-char-color3").trim(),
      this.rootStyles.getPropertyValue("--bar-line-char-color4").trim(),
      this.rootStyles.getPropertyValue("--white").trim(),
    ];
  }
}
